import { useState, useEffect, useCallback } from "react";
import { tokenUtils, sessionUtils } from "./securityUtils.js";
import { SECURITY_CONFIG } from "./securityConfig.js";
import useAuthStore from "../store/authStore.js";
import * as roleUtils from "./roleUtils.js";
/**
/** Hook for authentication state management */
export const useAuth = () => {
  const {
    user,
    isAuthenticated,
    isLoading,
    error,
    lastActivity,
    login,
    logout,
    resetIdleTimeout,
    rememberMe,
    changePasswordRequest,
    changePassword,
    setUser,
    setLoading,
    setError,
    clearError,
    updateLastActivity,
    refreshToken,
    restoreSession,
    getUserRole,
    isSessionValid,
  } = useAuthStore();

  // Optional: wrap actions in useCallback if you're passing them down
  const handleLogin = useCallback(login, []);
  const handleLogout = useCallback(logout, []);
  const handleRestoreSession = useCallback(restoreSession, []);
  const handleRefreshToken = useCallback(refreshToken, []);

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    lastActivity,
    login: handleLogin,
    logout: handleLogout,
    setUser,
    changePasswordRequest,
    changePassword,
    setLoading,
    setError,
    clearError,
    updateLastActivity,
    refreshToken: handleRefreshToken,
    restoreSession: handleRestoreSession,
    getUserRole,
    isSessionValid,
    resetIdleTimeout,
    rememberMe,
  };
};
//Removed - Migrating to Zustand store as single source of Auth truth
// const context = useContext(SecurityContext);

/**
 * Hook for role-based access control
 */
export const useRole = (requiredRoles = []) => {
  const { user, isAuthenticated } = useAuth();

  const hasRole = useCallback(
    (role) => {
      return roleUtils.hasRole(user, role);
    },
    [user]
  );

  const hasAnyRole = useCallback(
    (roles) => {
      return roleUtils.hasAnyRole(user, roles);
    },
    [user]
  );

  // Should be used in future if needed  / since now we dont have permission in response
  const hasPermission = useCallback(
    (permission) => {
      return roleUtils.hasPermission(user, permission);
    },
    [user]
  );

  const hasAnyPermission = useCallback(
    (permissions) => {
      return roleUtils.hasAnyPermission(user, permissions);
    },
    [user]
  );

  const hasAllPermissions = useCallback(
    (permissions) => {
      return roleUtils.hasAllPermissions(user, permissions);
    },
    [user]
  );

  const belongsToGroup = useCallback(
    (group) => {
      return roleUtils.belongsToGroup(user, group);
    },
    [user]
  );

  const belongsToAnyGroup = useCallback(
    (groups) => {
      return roleUtils.belongsToAnyGroup(user, groups);
    },
    [user]
  );

  const isAdmin = useCallback(
    (user) => {
      return roleUtils.isAdmin(user);
    },
    [user]
  );

  const isSuperAdmin = useCallback(
    (user) => {
      return roleUtils.isSuperAdmin(user);
    },
    [user]
  );

  const canAccessResource = useCallback(
    (requiredPermissions = [], requiredGroups = [], requireAll = false) => {
      return roleUtils.canAccessResource(
        user,
        requiredPermissions,
        requiredGroups,
        requireAll
      );
    },
    [user]
  );
  return {
    user,
    isAuthenticated,
    hasRole,
    hasAnyRole,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    belongsToGroup,
    belongsToAnyGroup,
    isAdmin,
    isSuperAdmin,
    canAccessResource,
    userRole: user?.role,
    userGroups: user?.groups || [],
    userPermissions: roleUtils.getUserPermissions(user),
  };
};

/**
 * Hook for session timeout management
 */
export const useSessionTimeout = () => {
  const { logout } = useAuth();
  const [timeUntilTimeout, setTimeUntilTimeout] = useState(null);
  const [showWarning, setShowWarning] = useState(false);

  const updateActivity = useCallback(() => {
    sessionUtils.updateLastActivity();
    setShowWarning(false);
  }, []);

  const handleTimeout = useCallback(() => {
    logout();
    setShowWarning(false);
  }, [logout]);

  const extendSession = useCallback(() => {
    updateActivity();
  }, [updateActivity]);

  useEffect(() => {
    const checkSession = () => {
      const lastActivity = sessionUtils.getLastActivity();
      if (!lastActivity) return;

      const timeSinceLastActivity = Date.now() - parseInt(lastActivity);
      const timeLeft =
        SECURITY_CONFIG.SESSION.IDLE_TIMEOUT - timeSinceLastActivity;

      setTimeUntilTimeout(Math.max(0, timeLeft));

      // Show warning before timeout
      if (timeLeft <= SECURITY_CONFIG.SESSION.TIMEOUT_WARNING && timeLeft > 0) {
        setShowWarning(true);
      }

      // Handle timeout
      if (timeLeft <= 0) {
        handleTimeout();
      }
    };

    // Initial check
    checkSession();

    // Set up interval to check session
    const interval = setInterval(
      checkSession,
      SECURITY_CONFIG.SESSION.CHECK_INTERVAL
    );

    // Update activity on user interaction
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    const handleUserActivity = () => {
      updateActivity();
    };

    events.forEach((event) => {
      document.addEventListener(event, handleUserActivity, true);
    });

    return () => {
      clearInterval(interval);
      events.forEach((event) => {
        document.removeEventListener(event, handleUserActivity, true);
      });
    };
  }, [updateActivity, handleTimeout]);

  return {
    timeUntilTimeout,
    showWarning,
    extendSession,
    handleTimeout,
  };
};

/**
 * Hook for token validation and refresh
 */
export const useTokenValidation = () => {
  const { refreshToken, logout } = useAuth();
  const [isValidating, setIsValidating] = useState(false);

  const validateToken = useCallback(async () => {
    const accessToken = tokenUtils.getAccessToken();

    if (!accessToken) {
      return false;
    }

    if (tokenUtils.isTokenExpired(accessToken)) {
      setIsValidating(true);

      try {
        const refreshResult = await refreshToken();
        setIsValidating(false);
        return refreshResult;
      } catch (error) {
        console.error("Token refresh failed:", error);
        setIsValidating(false);
        logout();
        return false;
      }
    }

    return true;
  }, [refreshToken, logout]);

  // Validate token on component mount
  useEffect(() => {
    validateToken();
  }, [validateToken]);

  return {
    validateToken,
    isValidating,
  };
};

/**
 * Hook for security headers management
 */
export const useSecurityHeaders = () => {
  const getAuthHeaders = useCallback(() => {
    const token = tokenUtils.getAccessToken();

    const headers = {
      [SECURITY_CONFIG.HEADERS.CONTENT_TYPE]: "application/json",
    };

    if (token) {
      headers[SECURITY_CONFIG.HEADERS.AUTHORIZATION] = `Bearer ${token}`;
    }

    return headers;
  }, []);

  const getCSRFToken = useCallback(() => {
    return tokenUtils.getCsrfToken();
  }, []);
  const getSecureHeaders = useCallback(() => {
    const headers = getAuthHeaders();
    const csrfToken = getCSRFToken();

    if (csrfToken) {
      headers[SECURITY_CONFIG.HEADERS.CSRF_TOKEN] = csrfToken;
    }

    return headers;
  }, [getAuthHeaders, getCSRFToken]);

  return {
    getAuthHeaders,
    getSecureHeaders,
    getCSRFToken,
  };
};

/**
 * Hook for permission-based component rendering
 */ export const usePermission = (requiredPermission, context = null) => {
  const { user, hasPermission: hasGlobalPermission } = useRole();
  if (context && process.env.NODE_ENV === "development") {
    console.warn(
      "[PERMISSION CONTEXT IGNORED] Object-level permissions not yet implemented. Context:",
      context
    );
  }

  const canAccess = hasGlobalPermission(requiredPermission);

  const PermissionGuard = useCallback(
    ({ children, fallback = null }) => {
      return canAccess ? children : fallback;
    },
    [canAccess]
  );

  return {
    canAccess,
    PermissionGuard,
    context,
  };
};

export default {
  useAuth,
  useRole,
  useSessionTimeout,
  useTokenValidation,
  useSecurityHeaders,
  usePermission,
};
